/**
 * AICreditsWidget — compact monthly credit balance panel.
 *
 * Renders the current user's AI-credit snapshot (pulled from
 * `/api/me/ai-credits`) with:
 *   • large "remaining" numeral
 *   • allowance + bonus breakdown
 *   • progress bar with color that reddens as the pool drains
 *   • "Upgrade for more" CTA when < 20% remaining (or a member is Free)
 *   • reset date so users know when the pool refills
 *
 * Admins/moderators get a "Bypass" pill (they don't consume the pool).
 * Auto-polls every 30s so the number stays fresh after AI calls.
 */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent } from './ui/card';
import { Sparkles, TrendingUp, ShieldCheck, History, Plus } from 'lucide-react';
import CreditHistoryModal from './CreditHistoryModal';
import PurchaseCreditsModal from './PurchaseCreditsModal';

const TIER_LABEL = { free: 'Free', pro: 'Professional', business: 'Business', enterprise: 'Enterprise' };

export default function AICreditsWidget({ compact = false }) {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [simToggling, setSimToggling] = useState(false);

  const reload = async () => {
    try {
      const { data } = await axios.get(`${API}/me/ai-credits`);
      setSnapshot(data);
    } catch { /* non-fatal */ }
  };

  useEffect(() => {
    let cancel = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/me/ai-credits`);
        if (!cancel) setSnapshot(data);
      } catch {
        // Non-fatal — widget just stays empty if the call fails.
      }
    };
    load();
    const t = setInterval(load, 30_000);
    return () => { cancel = true; clearInterval(t); };
  }, [API]);

  if (!snapshot) return null;

  const { tier, allowance, bonus, used, remaining, reset_at, bypass } = snapshot;
  const totalPool = allowance + bonus;
  const pct = totalPool > 0 ? Math.min(100, Math.round((used / totalPool) * 100)) : 0;

  // Admin-only "simulate member" toggle — flips server-side bypass so
  // the QA admin sees real deductions instead of ghost logs.
  const isAdmin = user?.role === 'admin';
  const simActive = !!user?.sim_member_mode;
  const toggleSim = async () => {
    try {
      setSimToggling(true);
      await axios.post(`${API}/me/sim-member-mode`, { enabled: !simActive });
      // Optimistic — refresh page state so bypass flag updates. Simple
      // reload is fine here since this is a QA-only toggle.
      window.location.reload();
    } catch { setSimToggling(false); }
  };

  // Bar color transitions from green → amber → red as the pool drains.
  const barColor = pct < 60 ? 'bg-emerald-500' : pct < 85 ? 'bg-amber-500' : 'bg-red-500';
  const resetLabel = reset_at
    ? new Date(reset_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : '—';

  const showUpgrade = !bypass && (tier === 'free' || remaining < Math.max(5, Math.floor(totalPool * 0.2)));

  return (
    <>
      <Card data-testid="ai-credits-widget" className="relative overflow-hidden">
      <div className={`absolute inset-x-0 top-0 h-1 ${bypass ? 'bg-indigo-500' : barColor}`} />
      <CardContent className={compact ? 'p-3' : 'p-4'}>
        <div className="flex items-start justify-between gap-2 mb-2">
          <div>
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              <Sparkles className="w-3 h-3 text-amber-500" />
              AI Credits
            </div>
            <div className="text-[10px] text-muted-foreground mt-0.5">
              {TIER_LABEL[tier] || tier} tier · resets {resetLabel}
            </div>
          </div>
          {bypass && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-bold"
              data-testid="ai-credits-bypass"
              title="Admin & moderator role — you don't consume the member credit pool"
            >
              <ShieldCheck className="w-2.5 h-2.5" /> Bypass
            </span>
          )}
          {isAdmin && (
            <button
              type="button"
              onClick={toggleSim}
              disabled={simToggling}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold transition-colors ${simActive ? 'bg-rose-500 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-300'}`}
              title="Admin QA: treat me as a regular member so credits actually debit"
              data-testid="ai-credits-sim-toggle"
            >
              {simActive ? 'Sim: ON' : 'Sim: OFF'}
            </button>
          )}
        </div>

        {bypass ? (
          <>
            <div className="text-[11px] text-slate-600 dark:text-slate-300 mb-2">
              Operator role — AI tools don&apos;t debit the member pool. Your bonus balance is still tracked below.
            </div>
            <div className="flex items-baseline gap-1.5 mb-1.5">
              <span className="font-heading text-3xl font-extrabold tracking-tight" data-testid="ai-credits-remaining">
                {remaining.toLocaleString()}
              </span>
              <span className="text-xs text-muted-foreground">
                / {totalPool.toLocaleString()} credits available
              </span>
            </div>
            <div className="mt-1 flex items-center justify-between text-[11px] text-muted-foreground">
              <span>Bonus pool: {bonus.toLocaleString()}</span>
              <span>Monthly allowance: {allowance.toLocaleString()}</span>
            </div>
            <button
              type="button"
              onClick={() => setPurchaseOpen(true)}
              className="mt-2 w-full flex items-center justify-center gap-1.5 rounded-md bg-amber-500 hover:bg-amber-500/90 text-navy text-xs font-bold py-1.5 transition-colors"
              data-testid="ai-credits-buy-more"
            >
              <Plus className="w-3 h-3" /> Buy more credits
            </button>
          </>
        ) : (
          <>
            <div className="flex items-baseline gap-1.5 mb-1.5">
              <span className="font-heading text-3xl font-extrabold tracking-tight" data-testid="ai-credits-remaining">
                {remaining.toLocaleString()}
              </span>
              <span className="text-xs text-muted-foreground">
                / {totalPool.toLocaleString()} credits left
              </span>
            </div>

            <div className="h-1.5 w-full rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${barColor}`}
                style={{ width: `${100 - pct}%` }}
              />
            </div>

            <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
              <span>Used {used.toLocaleString()} this month</span>
              {bonus > 0 && (
                <span className="text-primary font-semibold" data-testid="ai-credits-bonus">
                  + {bonus.toLocaleString()} bonus
                </span>
              )}
            </div>

            {showUpgrade && (
              <button
                type="button"
                onClick={() => navigate('/pricing')}
                className="mt-2.5 w-full flex items-center justify-center gap-1.5 rounded-md bg-primary/10 hover:bg-primary/20 text-primary text-xs font-semibold py-1.5 transition-colors"
                data-testid="ai-credits-upgrade-cta"
              >
                <TrendingUp className="w-3 h-3" />
                {tier === 'free' ? 'Get more credits with Pro' : 'Upgrade for more credits'}
              </button>
            )}

            {/* One-tap credit top-up for members who don't want to change
                tier. Opens the PurchaseCreditsModal (currently MOCKED —
                grants credits without a card charge until Stripe lands). */}
            <button
              type="button"
              onClick={() => setPurchaseOpen(true)}
              className="mt-2 w-full flex items-center justify-center gap-1.5 rounded-md bg-amber-500 hover:bg-amber-500/90 text-navy text-xs font-bold py-1.5 transition-colors"
              data-testid="ai-credits-buy-more"
            >
              <Plus className="w-3 h-3" /> Buy more credits
            </button>
          </>
        )}

        {/* History link available to everyone — even admins may want to
            audit their own operator activity even though they don't
            debit the member pool. */}
        <button
          type="button"
          onClick={() => setHistoryOpen(true)}
          className="mt-2 w-full flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
          data-testid="ai-credits-view-history"
        >
          <History className="w-3 h-3" /> View credit history
        </button>
      </CardContent>
    </Card>
    <CreditHistoryModal open={historyOpen} onOpenChange={setHistoryOpen} />
    <PurchaseCreditsModal
      open={purchaseOpen}
      onClose={() => setPurchaseOpen(false)}
      onPurchased={reload}
    />
    </>
  );
}
